import React, { useCallback } from 'react';
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPlus, faTrash } from '@fortawesome/fontawesome-free-solid/';

import { TextInput } from '../TextInput';
import { SelectInput } from '../SelectInput';

import config from "config";
import i18next from 'i18next';
import { validateChecklistTimerConfig } from "./checklist_error_helper";

const correctiveActionTypeOptions = [
    { label: "Restart Checklist", value: "RESTART_LIST" },
    //{ label: "Restart Task", value: "RESTART_TASK" },
    //{ label: "Fail Task", value: "FAIL_TASK" },
];

const notificationTypeOptions = [
    { label: "Before Duration Expires", value: "BEFORE_EXPIRE" },
    { label: "After Task Starts", value: "AFTER_START" },
];

const ChecklistTaskTimerNotificationRow = ({ readOnly, lastEntry, notification, onUpdate, onRemoveNotification }) => {
    const updateType = useCallback((value) => {
        onUpdate({ ...notification, notificationType: value.value });
    }, [notification, onUpdate]);

    const updateTimeframe = useCallback((value) => {
        onUpdate({ ...notification, value: parseInt(value) });
    }, [notification, onUpdate]);

    return (
        <li className="col-sm-12">
            <div className="col-sm-3">
                <TextInput
                    input={{
                        value: notification.value ? notification.value : "",
                        onChange: (event) => updateTimeframe(event.target.value),
                    }}
                    type="text"
                    label={"Minutes"}
                    readOnly={readOnly}
                    meta={notification.errors && notification.errors.value ? { touched: true, error: notification.errors.value } : {}}
                />
            </div>
            <div className="col-sm-7">
                <label>Type</label>
                <div style={{ paddingTop: 3 }}>
                    <SelectInput
                        input={{
                            value: notificationTypeOptions.find((option) => option.value === notification.notificationType),
                            onChange: (value) => updateType(value),
                        }}
                        isDisabled={readOnly}
                        options={notificationTypeOptions}
                        isSearchable={true}
                        meta={notification.errors && notification.errors.notificationType ? { touched: true, error: notification.errors.notificationType } : {}}
                    />
                </div>
            </div>
            <div className="col-sm-2 pull-right">
                <label>&nbsp;</label>
                {!readOnly && !lastEntry && (
                    <button className="btn btn-sm btn-link" onClick={onRemoveNotification}>
                        <img src={`${config.WEB_BASE_URL}/images/icon/delete-trash-can.svg`} name="delete" alt={i18next.t("delete")} />
                    </button>
                )}
            </div>
        </li>
    );
};

const ChecklistTaskTimer = ({ originalTimerConfig, readOnly, onTimerUpdate, onDismiss }) => {
    const timerConfig = originalTimerConfig ?? {
        duration: 60,
        max: 3,
        correctiveActionType: correctiveActionTypeOptions[0].value,
        checklistTaskTimerNotificationConfigs: [{ notificationType: notificationTypeOptions[0].value, value: 10 }],
        default: true
    };

    const updateNotification = useCallback((updatedNotification, index) => {
        let updatedTimer = { ...timerConfig, default: false, errors: {} };
        updatedTimer.checklistTaskTimerNotificationConfigs[index] = { ...updatedNotification, errors: {} };
        onTimerUpdate(updatedTimer);
    }, [timerConfig, onTimerUpdate]);

    const addNotification = useCallback(() => {
        let updatedTimer = { ...timerConfig, default: false, errors: {} };
        updatedTimer.checklistTaskTimerNotificationConfigs.push({ notificationType: notificationTypeOptions[0].value, value: 0, errors: {} });
        onTimerUpdate(updatedTimer);
    }, [timerConfig, onTimerUpdate]);

    const removeNotification = useCallback((index) => {
        let updatedTimer = { ...timerConfig, default: false, errors: {} };
        updatedTimer.checklistTaskTimerNotificationConfigs.splice(index, 1);
        onTimerUpdate(updatedTimer);
    }, [timerConfig, onTimerUpdate]);

    const validateAndDismiss = useCallback(() => {
        if (timerConfig.default) {
            onTimerUpdate(null);
            onDismiss();
        } else {
            const { hasErrors, updatedTimer } = validateChecklistTimerConfig(timerConfig);

            if (hasErrors) {
                onTimerUpdate(updatedTimer);
            } else {
                onDismiss();
            }
        }
    }, [timerConfig, onDismiss, onTimerUpdate]);

    const clearAndDismiss = useCallback(() => {
        onTimerUpdate(null);
        onDismiss();
    }, [onDismiss, onTimerUpdate]);

    return (
        <div className="row checklist-config-task-step-box">
            <div className="row checklist-header-title">
                <ul className="list-inline clearfix">
                    <li className="pull-left">
                        <button className="btn btn-sm btn-link" style={{ color: "#fff" }} onClick={validateAndDismiss}>
                            <FontAwesomeIcon icon={faArrowLeft} /> {i18next.t("pages.checklists.back-to-steps")}
                        </button>
                    </li>
                    <li className="pull-left">
                        <h4>Task Timer</h4>
                    </li>
                    {!readOnly && (
                        <li className="pull-right">
                            <button className="btn btn-sm btn-danger" style={{ color: "#fff" }} onClick={clearAndDismiss}>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </li>
                    )}
                </ul>
            </div>

            <div className="row top-row">
                <div className="col-sm-12 checklist-config-task-step-details-box" style={{ height: 600, overflow: "auto" }}>
                    <div className="form-group col-sm-3">
                        <TextInput
                            input={{
                                value: timerConfig.duration ? timerConfig.duration : "",
                                onChange: (event) => onTimerUpdate({ ...timerConfig, duration: event.target.value, default: false }),
                            }}
                            type="text"
                            label={"Duration"}
                            readOnly={readOnly}
                            meta={timerConfig.errors && timerConfig.errors.duration ? { touched: true, error: timerConfig.errors.duration } : {}}
                        />
                    </div>
                    <div className="form-group col-sm-3">
                        <TextInput
                            input={{
                                value: timerConfig.max ? timerConfig.max : "0",
                                onChange: (event) => onTimerUpdate({ ...timerConfig, max: event.target.value, default: false }),
                            }}
                            type="text"
                            label={"Max Repetitions"}
                            readOnly={readOnly}
                            meta={timerConfig.errors && timerConfig.errors.max ? { touched: true, error: timerConfig.errors.max } : {}}
                        />
                    </div>
                    <div className="form-group col-sm-6">
                        <label>PRE-EXPIRATION TIMER CORRECTIVE ACTION</label>
                        <div style={{ paddingTop: 3 }}>
                            <SelectInput
                                input={{
                                    value: correctiveActionTypeOptions.find((option) => option.value === timerConfig.correctiveActionType),
                                    onChange: (value) => onTimerUpdate({ ...timerConfig, correctiveActionType: value.value, default: false }),
                                }}
                                isDisabled={readOnly}
                                options={correctiveActionTypeOptions}
                                isSearchable={true}
                                meta={timerConfig.errors && timerConfig.errors.correctiveActionType ? { touched: true, error: timerConfig.errors.correctiveActionType } : {}}
                            />
                        </div>
                    </div>

                    <div className="col-sm-12">
                        <label>Notifications</label>
                        <ul className="list-inline clearfix">
                            {timerConfig.checklistTaskTimerNotificationConfigs.map((notification, index) => (
                                <ChecklistTaskTimerNotificationRow
                                    key={index}
                                    readOnly={readOnly}
                                    notification={notification}
                                    onUpdate={(updatedNotification) => updateNotification(updatedNotification, index)}
                                    onRemoveNotification={() => removeNotification(index)}
                                    lastEntry={timerConfig.checklistTaskTimerNotificationConfigs.length <= 1}
                                />
                            ))}
                        </ul>

                        {!readOnly && (
                            <button className="btn btn-sm btn-default" onClick={addNotification}>
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChecklistTaskTimer;